/**
 * Adaptador de Google Maps JavaScript para el contrato `MapAdapter`.
 *
 * Decisiones (ver `specs/rastreo-vista/02-PLAN.md`):
 * - Marcadores `google.maps.Marker` con un símbolo circular. Los marcadores
 *   avanzados exigen un `map ID`, y la especificación de mapas pide no
 *   introducirlo sin revisar su impacto en la facturación.
 * - Un marcador por vehículo, que se mueve con `setPosition`: los refrescos de
 *   posición no crean ni el mapa ni los marcadores otra vez (prueba M03).
 * - El recorrido es una polilínea con los puntos registrados, tal cual.
 */

import type { MapAdapter, MapStatus, MapTrackPoint, MapVehicle, MapViewState } from "./map-adapter.ts";
import {
  loadGoogleMaps,
  onGoogleAuthFailure,
  type GoogleMap,
  type GoogleMapsListener,
  type GoogleMapsNamespace,
  type GoogleMarker,
  type GoogleMarkerLabel,
  type GooglePolyline,
  type GoogleSymbol,
} from "./google-maps-loader.ts";

/** Colores de la identidad de GuardIA. El estado también va en el título del marcador, no solo en el color. */
const COLOR = {
  fresh: "#00aeef",
  stale: "#8a9aa8",
  unknown: "#8a9aa8",
  stroke: "#0b1e2d",
  track: "#1565c0",
} as const;

/** Centro inicial mientras no hay datos: Colombia completa. */
const COLOMBIA = { lat: 4.6, lng: -74.1 };

type Loader = (apiKey: string) => Promise<GoogleMapsNamespace>;

export class GoogleMapsAdapter implements MapAdapter {
  private maps: GoogleMapsNamespace | null = null;
  private map: GoogleMap | null = null;
  private markers = new Map<string, { marker: GoogleMarker; listener: GoogleMapsListener; vehicle: MapVehicle }>();
  /** Lo último que pidió la pantalla. Si el mapa aún no está listo, se dibuja al terminar de cargar. */
  private vehicles: MapVehicle[] = [];
  /** Una línea por tramo continuo del recorrido. Se reutilizan: refrescar no las recrea. */
  private tracks: GooglePolyline[] = [];
  private trackPoints: MapTrackPoint[] = [];
  private selectedId: string | null = null;
  private listeners = new Set<(status: MapStatus) => void>();
  private status: MapStatus = { state: "loading" };
  private stopAuthWatch: (() => void) | null = null;
  private disposed = false;

  constructor(
    private apiKey: string,
    private onSelect: (id: string) => void,
    private loader: Loader = loadGoogleMaps,
  ) {}

  async initialize(container: HTMLElement): Promise<void> {
    this.emit({ state: "loading" });
    this.stopAuthWatch = onGoogleAuthFailure(() =>
      this.emit({
        state: "failed",
        reason: "Google rechazó la clave del mapa. Los datos siguen disponibles abajo.",
      }),
    );
    try {
      this.maps = await this.loader(this.apiKey);
    } catch (error) {
      console.error("google maps: carga fallida", error);
      this.emit({ state: "failed", reason: "El mapa no cargó. Los datos siguen disponibles abajo." });
      return;
    }
    if (this.disposed) return;
    try {
      this.map = new this.maps.Map(container, {
        center: COLOMBIA,
        zoom: 6,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        clickableIcons: false,
      });
    } catch (error) {
      console.error("google maps: no se pudo crear el mapa", error);
      this.emit({ state: "failed", reason: "El mapa no cargó. Los datos siguen disponibles abajo." });
      return;
    }
    this.emit({ state: "ready" });
    // Lo que llegó antes de que el mapa estuviera listo se dibuja ahora.
    this.renderVehicles(this.vehicles);
    this.renderRecordedTrack(this.trackPoints);
  }

  renderVehicles(vehicles: MapVehicle[]): void {
    this.vehicles = vehicles;
    const maps = this.maps;
    const map = this.map;
    if (!maps || !map) return;
    const seen = new Set<string>();
    for (const vehicle of vehicles) {
      seen.add(vehicle.id);
      const existing = this.markers.get(vehicle.id);
      if (existing) {
        existing.marker.setPosition({ lat: vehicle.latitude, lng: vehicle.longitude });
        existing.marker.setIcon(this.icon(vehicle));
        existing.marker.setLabel(this.label(vehicle));
        existing.marker.setTitle(this.title(vehicle));
        existing.marker.setZIndex(vehicle.id === this.selectedId ? 10 : 1);
        existing.vehicle = vehicle;
        continue;
      }
      const marker = new maps.Marker({
        map,
        position: { lat: vehicle.latitude, lng: vehicle.longitude },
        title: this.title(vehicle),
        icon: this.icon(vehicle),
        label: this.label(vehicle),
        zIndex: vehicle.id === this.selectedId ? 10 : 1,
      });
      const listener = marker.addListener("click", () => this.onSelect(vehicle.id));
      this.markers.set(vehicle.id, { marker, listener, vehicle });
    }
    for (const [id, item] of this.markers) {
      if (seen.has(id)) continue;
      item.listener.remove();
      item.marker.setMap(null);
      this.markers.delete(id);
    }
  }

  renderRecordedTrack(points: MapTrackPoint[]): void {
    this.trackPoints = points;
    const maps = this.maps;
    const map = this.map;
    if (!maps || !map) return;
    // Un punto con `breakBefore` abre un tramo nuevo: el hueco no se une con una recta.
    const segments: Array<Array<{ lat: number; lng: number }>> = [];
    let current: Array<{ lat: number; lng: number }> | null = null;
    for (const point of points) {
      if (!current || point.breakBefore) {
        current = [];
        segments.push(current);
      }
      current.push({ lat: point.latitude, lng: point.longitude });
    }
    if (segments.length === 0) segments.push([]);
    segments.forEach((path, index) => {
      const existing = this.tracks[index];
      if (existing) {
        existing.setPath(path);
        return;
      }
      this.tracks.push(
        new maps.Polyline({
          map,
          path,
          strokeColor: COLOR.track,
          strokeOpacity: 0.85,
          strokeWeight: 4,
        }),
      );
    });
    // Los tramos que sobran se quitan del mapa.
    for (const extra of this.tracks.splice(segments.length)) extra.setMap(null);
  }

  setSelectedVehicle(id: string | null): void {
    this.selectedId = id;
    for (const item of this.markers.values()) {
      item.marker.setIcon(this.icon(item.vehicle));
      item.marker.setLabel(this.label(item.vehicle));
      item.marker.setZIndex(item.vehicle.id === id ? 10 : 1);
    }
  }

  fitToContent(): void {
    if (!this.maps || !this.map) return;
    const bounds = new this.maps.LatLngBounds();
    const selected = this.vehicles.find((vehicle) => vehicle.id === this.selectedId);
    if (this.trackPoints.length > 0) {
      for (const point of this.trackPoints) bounds.extend({ lat: point.latitude, lng: point.longitude });
      if (selected) bounds.extend({ lat: selected.latitude, lng: selected.longitude });
    } else if (selected) {
      this.map.setCenter({ lat: selected.latitude, lng: selected.longitude });
      this.map.setZoom(16);
      return;
    } else {
      for (const vehicle of this.vehicles) bounds.extend({ lat: vehicle.latitude, lng: vehicle.longitude });
    }
    if (!bounds.isEmpty()) this.map.fitBounds(bounds, 48);
  }

  panTo(point: MapTrackPoint): void {
    this.map?.panTo({ lat: point.latitude, lng: point.longitude });
  }

  captureViewState(): MapViewState | null {
    const center = this.map?.getCenter();
    const zoom = this.map?.getZoom();
    if (!center || zoom === undefined) return null;
    return { latitude: center.lat(), longitude: center.lng(), zoom };
  }

  restoreViewState(state: MapViewState): void {
    this.map?.setCenter({ lat: state.latitude, lng: state.longitude });
    this.map?.setZoom(state.zoom);
  }

  subscribeStatus(callback: (status: MapStatus) => void): () => void {
    this.listeners.add(callback);
    callback(this.status);
    return () => this.listeners.delete(callback);
  }

  dispose(): void {
    this.disposed = true;
    this.stopAuthWatch?.();
    for (const item of this.markers.values()) {
      item.listener.remove();
      item.marker.setMap(null);
    }
    this.markers.clear();
    for (const track of this.tracks) track.setMap(null);
    this.tracks = [];
    if (this.map && this.maps) this.maps.event.clearInstanceListeners(this.map);
    this.map = null;
    this.listeners.clear();
  }

  private emit(status: MapStatus) {
    this.status = status;
    for (const listener of this.listeners) listener(status);
  }

  private icon(vehicle: MapVehicle): GoogleSymbol {
    const selected = vehicle.id === this.selectedId;
    const Point = this.maps?.Point;
    return {
      path: this.maps?.SymbolPath.CIRCLE ?? 0,
      scale: selected ? 11 : 8,
      fillColor: COLOR[vehicle.freshness],
      fillOpacity: 1,
      strokeColor: COLOR.stroke,
      strokeWeight: selected ? 3 : 1.5,
      // El nombre va encima del círculo, como en Traccar: 2,6 radios hacia arriba.
      ...(Point ? { labelOrigin: new Point(0, -2.6) } : {}),
    };
  }

  /** El nombre del vehículo sobre su marcador. El estado sigue en el título, no solo en el color. */
  private label(vehicle: MapVehicle): GoogleMarkerLabel {
    return {
      text: vehicle.label.length > 24 ? `${vehicle.label.slice(0, 23)}…` : vehicle.label,
      color: COLOR.stroke,
      fontSize: vehicle.id === this.selectedId ? "13px" : "12px",
      fontWeight: vehicle.id === this.selectedId ? "700" : "600",
      className: "gd-map-label",
    };
  }

  private title(vehicle: MapVehicle): string {
    const estado = vehicle.freshness === "fresh" ? "posición reciente" : "posición vieja o sin confirmar";
    return `${vehicle.label} · ${estado}`;
  }
}
